/**
 * ThemeProvider — the palette and the accent, resolved once and read from
 * context instead of threaded through props.
 *
 * Two things travel together here and they scope DIFFERENTLY, which is the
 * whole reason this provider nests:
 *
 * - The **mode** (light / dark / follow the device) is ONE user preference for
 *   the whole app. Before this it was `useTheme()`, per-component `useState`
 *   with ten independent callers — so the `GymShell` copy and the `MainApp`
 *   copy did not agree, and the appearance setting left the shell's colours
 *   stale until a reload.
 * - The **accent** is per-surface. The gym selector, the global profile and
 *   the platform admin wear the platform accent; a gym's pages wear that gym's
 *   `accentColor`. One global accent would be wrong.
 *
 * So the root provider owns the mode, and a nested one overrides only what it
 * is given: `<ThemeProvider accent={gym.accentColor}>` re-paints its subtree's
 * accent and inherits the mode, while `<ThemeProvider mode="dark">` pins a
 * palette for a wall TV without touching the visitor's preference or the
 * accent above it.
 *
 * Only the root writes `<html data-theme>` and the body background. A nested
 * provider pinning a TV to dark must not repaint the browser chrome of the
 * admin who opened the preview.
 *
 * @see src/i18n/index.js — `LanguageProvider` is the same shape for language.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { getTheme } from "../utils";
import { DEFAULT_ACCENT } from "../constants";
import { localStore } from "../utils/safeStorage";

const STORAGE_KEY = "sendly-theme";

/** Resolve OS-level color scheme preference. */
function getSystemScheme() {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch { return "light"; }
}

/** Subscribe to OS color scheme changes. */
function subscribeSystemScheme(cb) {
  try {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", cb);
    return () => mq.removeEventListener("change", cb);
  } catch { return () => {}; }
}

/**
 * The stored theme preference ("light" / "dark" / "system"), without React.
 * Exported for the login bootstrap, which persists this session's preference
 * onto a brand-new user doc before any theme state exists to read.
 */
export function readStoredTheme() {
  return localStore.get(STORAGE_KEY) || "system";
}

/**
 * Read the resolved mode without React. For the handful of places that need a
 * palette before any provider is mounted — the boot screen and the crash
 * fallback, which render outside the app tree by construction.
 */
export function readTheme() {
  const stored = readStoredTheme();
  return stored === "system" ? getSystemScheme() : stored;
}

const ThemeContext = createContext(null);

/**
 * @param {string} [accent] - Override the accent for this subtree. Absent
 *   means inherit; the root falls back to `DEFAULT_ACCENT`.
 * @param {string} [mode] - Pin the palette ("light" / "dark") for this
 *   subtree, ignoring the user's preference. For surfaces the visitor does not
 *   own: a wall TV set by the gym, a fixed-light embed.
 */
export function ThemeProvider({ children, accent, mode }) {
  const outer = useContext(ThemeContext);
  const isRoot = !outer;

  const [userTheme, setUserThemeRaw] = useState(readStoredTheme);

  const setOwnUserTheme = useCallback((theme) => {
    setUserThemeRaw(theme);
    localStore.set(STORAGE_KEY, theme);
  }, []);

  // Live OS preference — re-renders when the device switches light/dark.
  const systemScheme = useSyncExternalStore(subscribeSystemScheme, getSystemScheme, () => "light");

  const inheritedTheme = outer ? outer.resolvedTheme : (userTheme === "system" ? systemScheme : userTheme);
  const resolvedTheme = mode === "light" || mode === "dark" ? mode : inheritedTheme;
  const th = getTheme(resolvedTheme);

  // The root owns the document: `<html data-theme>` so CSS can follow, and the
  // body background so iOS overscroll matches instead of flashing white.
  // `color-scheme` makes native controls (date / time pickers, scrollbars,
  // autofill) render in the matching scheme — without it the browser draws
  // picker icons black-on-dark, which is barely visible.
  const rootTheme = isRoot ? userTheme : null;
  const rootBg = isRoot ? th.bg : null;
  const rootResolved = isRoot ? resolvedTheme : null;
  useEffect(() => {
    if (!rootBg) return;
    try {
      if (rootTheme === "system") document.documentElement.removeAttribute("data-theme");
      else document.documentElement.setAttribute("data-theme", rootTheme);
      document.documentElement.style.colorScheme = rootResolved === "dark" ? "dark" : "light";
      document.body.style.background = rootBg;
    } catch {}
  }, [rootTheme, rootResolved, rootBg]);

  const outerAccent = outer ? outer.accent : DEFAULT_ACCENT;
  const outerUserTheme = outer ? outer.userTheme : null;
  const outerSetUserTheme = outer ? outer.setUserTheme : null;

  const value = useMemo(() => ({
    th,
    resolvedTheme,
    accent: accent || outerAccent,
    // The preference and its setter always belong to the root: an appearance
    // control rendered anywhere changes the app, never just its own subtree.
    userTheme: outerUserTheme ?? userTheme,
    setUserTheme: outerSetUserTheme ?? setOwnUserTheme,
  }), [th, resolvedTheme, accent, outerAccent, outerUserTheme, userTheme, outerSetUserTheme, setOwnUserTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * @returns {{ th: object, accent: string, resolvedTheme: string, userTheme: string, setUserTheme: (t: string) => void }}
 *
 * `th` is the resolved palette object, `resolvedTheme` the mode string it came
 * from. Both are here because components need both and used to receive them
 * under three different prop names (`th`, `theme`, `tvTheme`) — with `theme`
 * meaning the object in some components and the mode string in others.
 */
export function useAppTheme() {
  const ctx = useContext(ThemeContext);
  // No provider: the boot screen, a crash fallback, or a component rendered
  // in isolation by a test. A palette is better than a throw.
  if (!ctx) {
    const resolvedTheme = readTheme();
    return {
      th: getTheme(resolvedTheme),
      accent: DEFAULT_ACCENT,
      resolvedTheme,
      userTheme: readStoredTheme(),
      setUserTheme: () => {},
    };
  }
  return ctx;
}
